/**
 * Modern attacks media slider.
 * Mobile: horizontal scroll-snap.
 * Desktop (≥768px): CSS-grid stacked slides with opacity cross-fade;
 *   vertical scroll is hijacked to drive pagination while the slider is in view.
 */
( function () {
  'use strict';

  const ROOT_SEL = '[data-b3rg-modern-attacks="1"]';
  const MQ_MOBILE = '(max-width: 767px)';
  const MQ_DESKTOP = '(min-width: 768px)';

  /** @returns {boolean} */
  function isDesktop() {
    return window.matchMedia( MQ_DESKTOP ).matches;
  }

  /**
   * @param {HTMLElement} root
   * @returns {HTMLElement[]}
   */
  function getVisibleSlides( root ) {
    const all = Array.from(
      root.querySelectorAll( '.b3rg-modern-attacks__slide' )
    );
    if ( window.matchMedia( MQ_MOBILE ).matches ) {
      return all;
    }
    return all.filter( ( el ) => !el.hasAttribute( 'data-b3rg-mobile-only' ) );
  }

  /**
   * @param {HTMLElement} scroller
   * @param {HTMLElement} slide
   * @returns {number}
   */
  function scrollPositionForSlide( scroller, slide ) {
    return slide.offsetLeft;
  }

  /**
   * @param {HTMLElement} root
   * @param {number} index
   * @param {HTMLElement[]} visibleSlides
   * @param {boolean} smooth
   * @returns {number}
   */
  function goToIndex( root, index, visibleSlides, smooth ) {
    if ( visibleSlides.length === 0 ) {
      return 0;
    }
    const max = visibleSlides.length - 1;
    const i = Math.max( 0, Math.min( index, max ) );

    // Desktop uses CSS opacity cross-fade — no physical scrolling needed.
    if ( !isDesktop() ) {
      const scroller = root.querySelector( '.b3rg-modern-attacks__scroller' );
      if ( scroller ) {
        const slide = visibleSlides[ i ];
        const left = scrollPositionForSlide( scroller, slide );
        scroller.scrollTo( {
          left,
          behavior: smooth ? 'smooth' : 'auto',
        } );
      }
    }

    return i;
  }

  /**
   * @param {HTMLElement} pagination
   * @param {number} activeIndex
   */
  function syncPaginationActive( pagination, activeIndex ) {
    Array.from( pagination.querySelectorAll( '.b3rg-modern-attacks__bullet' ) ).forEach(
      ( b, j ) => {
        b.setAttribute( 'aria-selected', j === activeIndex ? 'true' : 'false' );
        b.classList.toggle( 'is-active', j === activeIndex );
      }
    );
  }

  /**
   * @param {HTMLElement} root
   * @param {HTMLElement[]} visibleSlides
   * @param {number} activeIndex
   */
  function syncSlideActive( root, visibleSlides, activeIndex ) {
    root.querySelectorAll( '.b3rg-modern-attacks__slide' ).forEach( ( s ) => {
      s.classList.remove( 'is-active' );
    } );
    const cur = visibleSlides[ activeIndex ];
    if ( cur ) {
      cur.classList.add( 'is-active' );
    }
  }

  /**
   * @param {HTMLElement} root
   */
  function initRoot( root ) {
    if ( root.getAttribute( 'data-b3rg-modern-attacks-init' ) === '1' ) {
      return;
    }
    root.setAttribute( 'data-b3rg-modern-attacks-init', '1' );

    const scroller = root.querySelector( '.b3rg-modern-attacks__scroller' );
    const pagination = root.querySelector( '.b3rg-modern-attacks__pagination' );
    const viewport = root.querySelector( '.b3rg-modern-attacks__viewport' );
    if ( !scroller || !pagination || !viewport ) {
      return;
    }

    let activeIndex = 0;
    let autoplayId = null;
    let scrollSyncRaf = 0;
    const autoplayMs = parseInt( root.getAttribute( 'data-autoplay' ) || '0', 10 );

    const readActiveFromScroll = () => {
      // Desktop uses fade, not scroll; active index is managed directly.
      if ( isDesktop() ) {
        return;
      }
      const visible = getVisibleSlides( root );
      if ( visible.length === 0 || scroller.clientWidth <= 0 ) {
        return;
      }
      const sl = scroller.scrollLeft;
      let best = 0;
      let bestDist = Infinity;
      visible.forEach( ( slide, i ) => {
        const left = scrollPositionForSlide( scroller, slide );
        const d = Math.abs( sl - left );
        if ( d < bestDist ) {
          bestDist = d;
          best = i;
        }
      } );
      if ( best !== activeIndex ) {
        activeIndex = best;
        syncPaginationActive( pagination, activeIndex );
        syncSlideActive( root, visible, activeIndex );
      }
    };

    const onScroll = () => {
      cancelAnimationFrame( scrollSyncRaf );
      scrollSyncRaf = requestAnimationFrame( readActiveFromScroll );
    };

    const onSelect = ( n, smooth ) => {
      const visible = getVisibleSlides( root );
      if ( visible.length === 0 ) {
        return;
      }
      activeIndex = goToIndex( root, n, visible, smooth !== false );
      syncPaginationActive( pagination, activeIndex );
      syncSlideActive( root, visible, activeIndex );
    };

    let layoutRetries = 0;

    const refresh = () => {
      if ( scroller.clientWidth === 0 && layoutRetries < 40 ) {
        layoutRetries += 1;
        requestAnimationFrame( refresh );
        return;
      }

      const visible = getVisibleSlides( root );
      if ( visible.length === 0 ) {
        return;
      }

      if ( activeIndex >= visible.length ) {
        activeIndex = visible.length - 1;
      }

      pagination.innerHTML = '';
      visible.forEach( ( _el, idx ) => {
        const btn = document.createElement( 'button' );
        btn.type = 'button';
        btn.className = 'b3rg-modern-attacks__bullet';
        btn.setAttribute( 'role', 'tab' );
        btn.setAttribute( 'aria-selected', idx === activeIndex ? 'true' : 'false' );
        btn.setAttribute(
          'aria-label',
          `Slide ${ String( idx + 1 ) } of ${ String( visible.length ) }`
        );
        btn.addEventListener( 'click', () => onSelect( idx, true ) );
        pagination.appendChild( btn );
      } );

      activeIndex = goToIndex( root, activeIndex, visible, false );
      syncPaginationActive( pagination, activeIndex );
      syncSlideActive( root, visible, activeIndex );
    };

    scroller.addEventListener( 'scroll', onScroll, { passive: true } );

    // --- Desktop: hijack vertical scroll to drive pagination ---
    // The non-passive wheel listener is added/removed via IntersectionObserver so it
    // only suppresses page scroll while this slider is actually in the viewport.
    let wheelCooldown = false;
    const WHEEL_COOLDOWN_MS = 750;

    const onWindowWheel = ( e ) => {
      if ( !isDesktop() ) {
        return;
      }
      const visible = getVisibleSlides( root );
      if ( visible.length < 2 ) {
        return;
      }
      const goingForward = e.deltaY > 0;
      const goingBack = e.deltaY < 0;

      // At the boundary in the scroll direction: release back to page scroll.
      if ( goingForward && activeIndex >= visible.length - 1 ) {
        return;
      }
      if ( goingBack && activeIndex <= 0 ) {
        return;
      }

      e.preventDefault();

      if ( wheelCooldown ) {
        return;
      }
      wheelCooldown = true;
      setTimeout( () => {
        wheelCooldown = false;
      }, WHEEL_COOLDOWN_MS );

      if ( goingForward ) {
        onSelect( activeIndex + 1, true );
      } else if ( goingBack ) {
        onSelect( activeIndex - 1, true );
      }
    };

    if ( typeof IntersectionObserver !== 'undefined' ) {
      const io = new IntersectionObserver(
        ( entries ) => {
          if ( entries[ 0 ].isIntersecting ) {
            window.addEventListener( 'wheel', onWindowWheel, { passive: false } );
          } else {
            window.removeEventListener( 'wheel', onWindowWheel );
          }
        },
        { threshold: 1.0 }
      );
      io.observe( root );
    }
    // -------------------------------------------------------

    refresh();
    window.addEventListener( 'load', refresh, { once: true } );

    const mq = window.matchMedia( MQ_MOBILE );
    mq.addEventListener( 'change', refresh );

    let resizeRaf = 0;
    window.addEventListener( 'resize', () => {
      cancelAnimationFrame( resizeRaf );
      resizeRaf = requestAnimationFrame( refresh );
    } );

    viewport.addEventListener( 'keydown', ( e ) => {
      const visible = getVisibleSlides( root );
      if ( visible.length < 2 ) {
        return;
      }
      if ( e.key === 'ArrowRight' ) {
        e.preventDefault();
        onSelect( ( activeIndex + 1 ) % visible.length, true );
      } else if ( e.key === 'ArrowLeft' ) {
        e.preventDefault();
        onSelect( ( activeIndex - 1 + visible.length ) % visible.length, true );
      }
    } );

    const startAutoplay = () => {
      if ( autoplayMs <= 0 ) {
        return;
      }
      stopAutoplay();
      autoplayId = window.setInterval( () => {
        const visible = getVisibleSlides( root );
        if ( visible.length < 2 ) {
          return;
        }
        onSelect( ( activeIndex + 1 ) % visible.length, true );
      }, autoplayMs );
    };

    const stopAutoplay = () => {
      if ( autoplayId !== null ) {
        window.clearInterval( autoplayId );
        autoplayId = null;
      }
    };

    root.addEventListener( 'mouseenter', stopAutoplay );
    root.addEventListener( 'mouseleave', startAutoplay );
    viewport.addEventListener( 'focus', stopAutoplay );
    viewport.addEventListener( 'blur', startAutoplay );
    startAutoplay();
  }

  function bootModernAttacksSliders() {
    document.querySelectorAll( ROOT_SEL ).forEach( initRoot );
  }

  if ( document.readyState === 'loading' ) {
    document.addEventListener( 'DOMContentLoaded', bootModernAttacksSliders );
  } else {
    bootModernAttacksSliders();
  }
} )();
